import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Database, HardDrive, Layers, FileStack, Loader2, RefreshCw, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 o';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return 'hier';
  return d.toLocaleDateString('fr-FR');
};

const ROLE_LABELS = { sa: 'Super Admin', soa: 'Sous-Admin', admin: 'Admin', rh: 'RH', commercial: 'Commercial' };
const ROLE_COLORS = {
  sa: 'bg-amber-100 text-amber-700', soa: 'bg-violet-100 text-violet-700', admin: 'bg-violet-100 text-violet-700',
  rh: 'bg-emerald-100 text-emerald-700', commercial: 'bg-slate-100 text-slate-700',
};

export const SystemHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/system/health`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) return <p className="text-red-600">Erreur de chargement des données système</p>;

  const disk = data.disk || {};
  const diskUsedPct = disk.total ? Math.round((disk.used / disk.total) * 100) : 0;
  const activeUsers = data.active_users || [];

  const overview = [
    { title: 'Taille de la base', value: formatBytes(data.db.total_size), icon: Database, color: 'bg-indigo-500', testid: 'db-total-size' },
    { title: 'Stockage réservé', value: formatBytes(data.db.storage_size), icon: HardDrive, color: 'bg-emerald-500', testid: 'db-storage-size' },
    { title: 'Documents (fiches)', value: (data.db.objects || 0).toLocaleString('fr-FR'), icon: FileStack, color: 'bg-violet-500', testid: 'db-objects' },
    { title: 'Collections', value: data.db.collections || 0, icon: Layers, color: 'bg-amber-500', testid: 'db-collections' },
  ];

  return (
    <div className="space-y-6" data-testid="system-health-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="system-health-title">
            Santé système
          </h1>
          <p className="text-slate-500 mt-1">Utilisateurs connectés, base de données et stockage</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-slate-100 text-slate-600" data-testid="env-badge">
            DB : {data.environment_hint}
          </Badge>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-health-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Connected users hero */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-white/15 backdrop-blur">
              <Users className="w-6 h-6 text-white" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-indigo-100 text-sm">Utilisateurs connectés</p>
              <p className="text-4xl font-bold text-white leading-tight" data-testid="active-users-count">
                {data.active_users_count || 0}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-indigo-100 text-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
            </span>
            Sessions actives (7 derniers jours)
          </div>
        </div>
        <div className="overflow-x-auto">
          {activeUsers.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">Aucun utilisateur connecté</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Filiale</TableHead>
                  <TableHead className="text-right">Dernière connexion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeUsers.map((u) => (
                  <TableRow key={u.user_id} data-testid={`active-user-${u.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                            {u.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{u.name}</p>
                          <p className="text-xs text-slate-400 truncate">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{u.agency_name}</TableCell>
                    <TableCell className="text-right text-sm text-slate-500">{formatRelative(u.last_login)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {overview.map((s) => (
          <Card key={s.title} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className={`inline-flex p-2.5 rounded-xl ${s.color}`}>
                <s.icon className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <p className="text-sm text-slate-500 mt-3">{s.title}</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1" data-testid={s.testid}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Disk usage */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope] text-lg flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-slate-500" /> Espace disque
          </CardTitle>
          <CardDescription>Disque mutualisé de l'infrastructure (géré par l'hébergeur, partagé — ne reflète pas la taille de votre app)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-slate-700">
              {formatBytes(disk.used)} utilisés sur {formatBytes(disk.total)}
            </span>
            <Badge className={diskUsedPct > 85 ? 'bg-rose-100 text-rose-700' : diskUsedPct > 60 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'} data-testid="disk-used-pct">
              {diskUsedPct}% utilisé
            </Badge>
          </div>
          <Progress value={diskUsedPct} className="h-3" />
          <p className="text-sm text-slate-500 mt-2" data-testid="disk-free">
            <span className="font-semibold text-emerald-600">{formatBytes(disk.free)}</span> encore disponibles
          </p>
        </CardContent>
      </Card>

      {/* Per-collection table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope] text-lg">Détail par collection</CardTitle>
          <CardDescription>Volume de chaque « tiroir » de la base (trié par taille)</CardDescription>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Collection</TableHead>
                <TableHead className="text-right">Documents</TableHead>
                <TableHead className="text-right">Données</TableHead>
                <TableHead className="text-right">Stockage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.collections.map((c) => (
                <TableRow key={c.name} data-testid={`collection-row-${c.name}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-right">{(c.count || 0).toLocaleString('fr-FR')}</TableCell>
                  <TableCell className="text-right">{formatBytes(c.size)}</TableCell>
                  <TableCell className="text-right text-slate-500">{formatBytes(c.storage_size)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <p className="text-xs text-slate-400">
        Généré le {new Date(data.generated_at).toLocaleString('fr-FR')}
      </p>
    </div>
  );
};

export default SystemHealth;
